'use client'

import { AlertTriangle, TimerReset } from 'lucide-react'
import type { Task, Sprint } from '@/lib/types'
import {
  taskInSprint, sprintWeeks, workload, onTimeScore, riskFlags, RISK_RANK, isoDay,
  type RiskCtx, type RiskKind, type BurndownPoint, type VelocityPoint,
} from '@/lib/dev-sprint'
import type { BoardData, DevDerived } from './types'
import { Burndown, VelocityBars, Heatmap } from './charts'
import { Avatar, RiskChip } from './ui'

const norm = (x?: string | null) => (x ?? '').trim().toLowerCase()

interface Props {
  board: BoardData
  sprint: Sprint
  derived: DevDerived
  riskCtx: RiskCtx
  burndownSeries: BurndownPoint[]
  velocityData: VelocityPoint[]
  capacity: number
  onOpenTask: (id: string) => void
}

export function MonitoringView({ board, sprint, derived, riskCtx, burndownSeries, velocityData, capacity, onOpenTask }: Props) {
  const { statusOf, projById } = derived
  const sprintTasks = board.tasks.filter((t) => taskInSprint(t, sprint))
  const usernames = board.team.map((m) => m.username ?? '').filter(Boolean)
  const weeks = sprintWeeks(sprint)
  const wl = workload(sprintTasks, sprint, usernames, statusOf)

  const heatmapRows = board.team
    .filter((m) => m.username)
    .map((m) => ({ name: m.name, load: wl[norm(m.username)] ?? new Array(weeks).fill(0) }))

  const scorecards = board.team
    .filter((m) => m.username)
    .map((m) => ({ member: m, sc: onTimeScore(sprintTasks, m.username as string, statusOf) }))

  // Risk radar: task open dengan minimal 1 flag, urut paling parah lalu due terdekat.
  const flagged: { task: Task; flags: RiskKind[] }[] = []
  for (const t of sprintTasks) {
    const flags = riskFlags(t, riskCtx)
    if (flags.length) flagged.push({ task: t, flags })
  }
  flagged.sort((a, b) => {
    const ra = RISK_RANK[a.flags[0]], rb = RISK_RANK[b.flags[0]]
    if (ra !== rb) return ra - rb
    return isoDay(a.task.dueDate).localeCompare(isoDay(b.task.dueDate))
  })
  const aging = flagged.filter((f) => f.flags.includes('aging'))

  return (
    <div className="space-y-4">
      <div className="grid lg:grid-cols-2 gap-4">
        <Card title="Burndown" subtitle="Sisa points vs ideal">
          <Burndown series={burndownSeries} todayISO={riskCtx.todayISO} />
        </Card>
        <Card title="Velocity" subtitle="Points selesai per sprint">
          <VelocityBars data={velocityData} />
        </Card>
      </div>

      <Card title="Workload" subtitle="Beban points per dev per minggu">
        <Heatmap rows={heatmapRows} weeks={weeks} capacity={capacity} />
      </Card>

      <Card title="On-time Scorecard" subtitle="Ketepatan waktu tiap dev di sprint ini">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {scorecards.map(({ member, sc }) => (
            <div key={member.email} className="rounded-xl border border-[#EDEBE5] p-3">
              <div className="flex items-center gap-2 mb-2">
                <Avatar name={member.name} size={26} />
                <span className="text-[13px] font-semibold text-[#1A1916] truncate">{member.name}</span>
                <span className="ml-auto text-[18px] font-extrabold tabular-nums" style={{ color: otColor(sc.onTimePct) }}>
                  {sc.onTimePct == null ? '—' : `${sc.onTimePct}%`}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <Stat label="Done" value={`${sc.done}`} />
                <Stat label="Open" value={`${sc.openCount}`} />
                <Stat label="Slip" value={sc.avgSlip ? `${sc.avgSlip}h` : '0'} />
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card title="Risk Radar" subtitle={`${flagged.length} task perlu perhatian`} icon={<AlertTriangle className="w-4 h-4 text-[#C0362C]" />}>
        {flagged.length === 0 ? (
          <p className="text-[13px] text-[#8B8A85] py-4 text-center">Tidak ada task berisiko. 🎉</p>
        ) : (
          <ul className="divide-y divide-[#F0EEE7]">
            {flagged.map(({ task, flags }) => {
              const p = projById.get(task.projectId)
              return (
                <li key={task.id}>
                  <button onClick={() => onOpenTask(task.id)} className="w-full flex items-center gap-2.5 py-2.5 text-left hover:bg-[#FAF9F6] rounded-lg px-2 -mx-2 transition-colors">
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: p?.color ?? '#D4A843' }} />
                    <span className="text-[13px] font-medium text-[#1A1916] truncate flex-1">{task.title}</span>
                    <span className="text-[11px] text-[#AEADA8] hidden sm:block truncate max-w-[110px]">{task.assignee || '—'}</span>
                    <span className="text-[11px] text-[#AEADA8] tabular-nums hidden md:block">{isoDay(task.dueDate) ? isoDay(task.dueDate).slice(5) : '—'}</span>
                    <span className="flex items-center gap-1 flex-shrink-0">{flags.slice(0, 3).map((f) => <RiskChip key={f} kind={f} />)}</span>
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </Card>

      {aging.length > 0 && (
        <Card title="Aging Tasks" subtitle="In-progress > 7 hari" icon={<TimerReset className="w-4 h-4 text-[#6B6A65]" />}>
          <ul className="divide-y divide-[#F0EEE7]">
            {aging.map(({ task }) => {
              const p = projById.get(task.projectId)
              return (
                <li key={task.id}>
                  <button onClick={() => onOpenTask(task.id)} className="w-full flex items-center gap-2.5 py-2.5 text-left hover:bg-[#FAF9F6] rounded-lg px-2 -mx-2 transition-colors">
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: p?.color ?? '#D4A843' }} />
                    <span className="text-[13px] font-medium text-[#1A1916] truncate flex-1">{task.title}</span>
                    <span className="text-[11px] text-[#AEADA8] truncate max-w-[110px]">{task.assignee || '—'}</span>
                  </button>
                </li>
              )
            })}
          </ul>
        </Card>
      )}
    </div>
  )
}

function otColor(pct: number | null): string {
  if (pct == null) return '#AEADA8'
  if (pct >= 80) return '#1E7A46'
  if (pct >= 50) return '#9A6B00'
  return '#C0362C'
}

function Card({ title, subtitle, icon, children }: { title: string; subtitle?: string; icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-[#E7E5DF] bg-white p-4">
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <div>
          <h3 className="text-[14px] font-bold text-[#1A1916] leading-tight">{title}</h3>
          {subtitle && <p className="text-[11px] text-[#AEADA8]">{subtitle}</p>}
        </div>
      </div>
      {children}
    </div>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-[#F7F6F2] py-1.5">
      <div className="text-[14px] font-bold text-[#1A1916] tabular-nums leading-none">{value}</div>
      <div className="text-[10px] text-[#AEADA8] mt-0.5">{label}</div>
    </div>
  )
}
